/**
 * Fixtures Phase - Main orchestrator component
 * Handles role-based views for fixture management
 */
import { useState, useEffect, useCallback } from 'react';
import { axiosInstance } from '../../config/axios';
import { derivePhaseBadge } from './phaseBadge';
import { toast } from 'sonner';

// Sub-components
import FixturesAdminPanel from './FixturesAdminPanel';
import FixturesListView from './FixturesListView';
import FixturesCalendarView from './FixturesCalendarView';
import StandingsTable from './StandingsTable';
import KnockoutBracket from './KnockoutBracket';

export default function FixturesPhase({ phase, event }) {
  const [fixtures, setFixtures] = useState([]);
  const [standings, setStandings] = useState([]);
  const [teams, setTeams] = useState([]);
  const [pointConfig, setPointConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState('list');
  const [currentUser, setCurrentUser] = useState(null);

  // Admin tools only for this event's admins (owner, co-admins, Super Admins)
  const isAdmin = !!event?.can_manage;

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Load user
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        try {
          setCurrentUser(JSON.parse(storedUser));
        } catch (e) {
          console.error('Failed to parse user:', e);
        }
      }

      const [fixturesRes, standingsRes, teamsRes, teamsSetupRes, pointsRes] = await Promise.all([
        axiosInstance.get(`/v1/events/${event.id}/fixtures`).catch(() => ({ data: [] })),
        axiosInstance.get(`/v1/events/${event.id}/fixtures/standings`).catch(() => ({ data: [] })),
        axiosInstance.get(`/v1/events/${event.id}/auction/teams`).catch(() => ({ data: [] })),
        axiosInstance.get(`/v1/events/${event.id}/team-setup/teams`).catch(() => ({ data: [] })),
        axiosInstance.get(`/v1/events/${event.id}/fixtures/points`).catch(() => ({ data: null })),
      ]);

      setFixtures(fixturesRes.data);
      setStandings(standingsRes.data);
      // When auction is enabled → /auction/teams returns the source-of-truth list.
      // When auction is disabled → /auction/teams returns 403 (data:[]), so fall back
      // to the team-setup roster which writes into the same auction_teams table.
      const auctionTeams = teamsRes.data || [];
      const setupTeams = teamsSetupRes.data || [];
      setTeams(auctionTeams.length > 0 ? auctionTeams : setupTeams);
      setPointConfig(pointsRes.data);
    } catch (err) {
      console.error('Failed to fetch fixtures data:', err);
      toast.error('Failed to load fixtures');
    } finally {
      setLoading(false);
    }
  }, [event.id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Auto-refresh standings + fixtures every 30s when any match is live.
  // This keeps the Admin view in sync as scores tick during live matches.
  useEffect(() => {
    const hasLive = (fixtures || []).some(f => f.match_status === 'live');
    if (!hasLive) return;
    const id = setInterval(() => { fetchData(); }, 30000);
    return () => clearInterval(id);
  }, [fixtures, fetchData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#3AAFA9]"></div>
      </div>
    );
  }

  // Check if we have knockout fixtures
  const hasKnockoutFixtures = fixtures.some(f => 
    ['quarter_final', 'semi_final', 'third_place', 'final', 'round_of_16'].includes(f.round_type)
  );

  // Shared phase badge (same as admin panel's so they stay in sync)
  const phaseBadge = derivePhaseBadge(fixtures);

  // View tabs
  const VIEW_TABS = [
    { key: 'list', label: 'Match List', icon: 'ph-list' },
    { key: 'calendar', label: 'Calendar', icon: 'ph-calendar' },
    { key: 'standings', label: 'Standings', icon: 'ph-list-numbers' },
    ...(hasKnockoutFixtures ? [{ key: 'bracket', label: 'Bracket', icon: 'ph-brackets-curly' }] : []),
  ];

  return (
    <div className="space-y-6" data-testid="fixtures-phase">
      {/* Admin Panel */}
      {isAdmin && (
        <FixturesAdminPanel
          eventId={event.id}
          event={event}
          fixtures={fixtures}
          teams={teams}
          pointConfig={pointConfig}
          onRefresh={fetchData}
        />
      )}

      {/* Phase status badge (visible to all viewers) */}
      {phaseBadge && (
        <div className="flex items-center" data-testid="public-phase-badge">
          <span
            className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full border ${phaseBadge.lightTone}`}
          >
            <i className={`ph-bold ${phaseBadge.icon}`}></i>
            {phaseBadge.label}
          </span>
        </div>
      )}

      {/* View Tabs */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="flex border-b border-gray-100">
          {VIEW_TABS.map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveView(tab.key)}
              data-testid={`fixtures-view-${tab.key}`}
              className={`flex-1 px-4 py-3 text-sm font-medium flex items-center justify-center gap-2 transition-colors ${
                activeView === tab.key
                  ? 'text-[#3AAFA9] border-b-2 border-[#3AAFA9] bg-[#3AAFA9]/5'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
              }`}
            >
              <i className={`ph-bold ${tab.icon}`}></i>
              <span className="hidden sm:inline">{tab.label}</span>
            </button>
          ))}
        </div>

        <div className="p-4">
          {fixtures.length === 0 && activeView !== 'standings' ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                <i className="ph-bold ph-calendar-x text-2xl text-gray-400"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-700 mb-2">No Fixtures Yet</h3>
              <p className="text-sm text-gray-500">
                {isAdmin 
                  ? 'Use the admin panel above to generate or create fixtures.'
                  : 'Match fixtures will be displayed here once available.'}
              </p>
            </div>
          ) : (
            <>
              {activeView === 'list' && (
                <FixturesListView 
                  fixtures={fixtures} 
                  isAdmin={isAdmin}
                  eventId={event.id}
                  onRefresh={fetchData}
                />
              )}
              {activeView === 'calendar' && (
                <FixturesCalendarView 
                  eventId={event.id}
                  fixtures={fixtures}
                />
              )}
              {activeView === 'standings' && (
                <StandingsTable 
                  standings={standings}
                  pointConfig={pointConfig}
                  isAdmin={isAdmin}
                  eventId={event.id}
                  onRefresh={fetchData}
                />
              )}
              {activeView === 'bracket' && (
                <KnockoutBracket eventId={event.id} />
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
